import {
	Box,
	Button,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm, type UseFormReset } from "react-hook-form";
import { toast } from "src/Toaster/Toaster";
import type { IAccountDetails, ICreateAccount } from "src/Types/account.types";

interface Props {
	onSubmit: (
		data: ICreateAccount,
		reset: UseFormReset<ICreateAccount>
	) => void;
	isLoading: boolean;
	previousData?: IAccountDetails;
}

const AccountForm = ({ onSubmit, isLoading, previousData }: Props) => {
	const { control, register, handleSubmit, reset, formState } =
		useForm<ICreateAccount>({
			...(previousData && {
				defaultValues: {
					ac_name: previousData.ac_name,
					ac_type: previousData.ac_type,
					ac_number: previousData.ac_number,
					ac_bank_name: previousData.ac_bank_name,
					ac_bank_branch: previousData.ac_bank_branch,
					opening_balance: previousData.opening_balance,
					ac_details: previousData.ac_details,
				},
			}),
		});

	useEffect(() => {
		const firstError = Object.values(formState.errors).find(
			(error) => error?.message
		);

		if (firstError?.message) {
			toast.fire({
				icon: "error",
				title: "Missing Info!",
				text: firstError.message,
				position: "top-right",
			});
		}
	}, [
		formState.errors.ac_name,
		formState.errors.ac_type,
		formState.errors.opening_balance,
	]);

	return (
		<Box
			component='form'
			onSubmit={handleSubmit((data) => {
				onSubmit(data, reset);
			})}
			sx={{ p: 2, mt: 1 }}
		>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						label='Account Name'
						{...register("ac_name", {
							required: {
								value: true,
								message: "Please, Provide an account name!",
							},
						})}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<FormControl fullWidth required>
						<InputLabel id='ac-type-label'>Account Type</InputLabel>
						<Controller
							control={control}
							name='ac_type'
							defaultValue='Bank'
							rules={{ required: "Account Type is required!" }}
							render={({ field }) => (
								<Select
									labelId='ac-type-label'
									label='Account Type'
									{...field}
								>
									<MenuItem value='Bank'>Bank</MenuItem>
									<MenuItem value='Cash'>Cash</MenuItem>
									<MenuItem value='Mobile Banking'>
										Mobile Banking
									</MenuItem>
								</Select>
							)}
						/>
					</FormControl>
				</Grid>

				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						label='Account Number'
						{...register("ac_number")}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						label='Bank Name'
						{...register("ac_bank_name", {
							required: {
								value: true,
								message: "Please, Provide an Bank Name!",
							},
						})}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						label='Bank Branch'
						{...register("ac_bank_branch")}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextField
						fullWidth
						label='Opening Balance'
						type='number'
						disabled={!!previousData}
						{...register("opening_balance", {
							valueAsNumber: true,
							required: {
								value: true,
								message: "Please provide opening balance!",
							},
							min: {
								value: 0,
								message: "Balance must be a positive number!",
							},
						})}
					/>
				</Grid>
				<Grid item xs={12}>
					<TextField
						fullWidth
						label='Account Details'
						multiline
						rows={4}
						placeholder='Write additional details (optional)...'
						{...register("ac_details")}
					/>
				</Grid>
			</Grid>

			<Button
				type='submit'
				fullWidth
				variant='contained'
				sx={{ mt: 3 }}
				disabled={isLoading}
			>
				{isLoading
					? previousData
						? "Updating..."
						: "Creating..."
					: previousData
					? "Update Account"
					: "Create Account"}
			</Button>
		</Box>
	);
};

export default AccountForm;
